<template>
  <form class="needs-validation row mx-0 mb-3" @submit.prevent="confirm" novalidate>
    <section class="col-12 col-md bg-white rounded p-4 p-xl-5">
      <div role="alert" class="alert alert-warning" v-if="errors">{{errors}}</div>
      <h1 class="h3 mb-2">Your Details</h1>
      <p class="mb-4">It looks like some details are missing from your <strong>profile</strong>. <br>Please complete the necessary fields.</p>
      <div class="row mb-3">

        <div class="col-12 col-md-6 row no-gutters pr-xl-4">
          <div class="col-12 col-md-auto form-group">
            <label for="title">Title</label>
            <select class="form-control" id="title" required v-model="userInfo.title">
              <option disabled></option>
              <option value="Mr">Mr</option>
              <option value="Ms">Ms</option>
              <option value="Miss">Miss</option>
              <option value="Mrs">Mrs</option>
              <option value="Dr">Dr</option>
            </select>
            <div class="invalid-tooltip">Please include your title.</div>
          </div>

          <div class="col-12 col-md pl-md-4 form-group">
            <label for="firstName">First Name</label>
            <input type="text" class="form-control" id="firstName" maxlength="40" required v-model="userInfo.first_name">
            <div class="invalid-tooltip">Please include your First Name.</div>
          </div>
        </div>

        <div class="col-12 col-md-6 pl-xl-4 form-group">
          <label for="lastName">Last Name</label>
          <input type="text" class="form-control" id="lastName" maxlength="40" required v-model="userInfo.last_name">
          <div class="invalid-tooltip">Please include your Last Name.</div>
        </div>
        <div class="col-12 col-md-6 pr-xl-4 form-group">
          <label for="dateOfBirth">Date of Birth</label>
          <input v-model="userInfo.dob" :max="today()" required :class="['form-control form-date', errorStatus === 'validation_error' ? 'is-invalid' : '']" id="dateOfBirth" type="date" :placeholder="[isLegacy() ? 'yyyy-mm-dd' : 'dd/mm/yyyy']" pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}" value="1988-01-01">
          <div class="invalid-tooltip">Please include your Date of Birth{{ isLegacy() ? ' in the format yyyy-mm-dd' : ''}}.</div>
        </div>
        <div class="col-12 col-md-6 pl-xl-4 form-group">
          <label for="gender">Gender</label>
          <select class="form-control" id="gender" required v-model="userInfo.gender">
            <option disabled></option>
            <option>Male</option>
            <option>Female</option>
            <option>Unspecified</option>
          </select>
          <div class="invalid-tooltip">Please include your Gender.</div>
        </div>

        <div v-if="userInfo.attributes.address" class="row w-100 mx-0">
          <div class="col-12 col-md-6 pr-xl-4 form-group">
            <label for="address">Address Line 1</label>
            <input type="text" class="form-control" id="address" maxlength="80" required pattern=".{5,}" v-model="userInfo.attributes.address.street_1">
            <div class="invalid-tooltip">Please include an address of at least 5 characters.</div>
            <small class="d-block">Must be at least 5 characters</small>
          </div>
          <div class="col-12 col-md-6 pl-xl-4 form-group">
            <label for="address2">Address Line 2 <small>(optional)</small></label>
            <input type="text" class="form-control" id="address2" maxlength="80" v-model="userInfo.attributes.address.street_2">
            <div class="invalid-tooltip">Please include an address of at least 5 characters.</div>
          </div>

          <div class="col-12 col-md-6 pr-xl-4 form-group">
            <label for="suburb">Suburb</label>
            <input type="text" class="form-control" id="suburb" maxlength="40" required v-model="userInfo.attributes.address.suburb">
            <div class="invalid-tooltip">Please include your Suburb.</div>
          </div>

          <div class="col-12 col-md-6 form-group pl-xl-4 mb-4">
            <label for="postcode">Postcode</label>
            <input type="text" class="form-control" id="postcode" maxlength="4" required pattern="\d{4}" v-model="userInfo.attributes.address.postcode">
            <div class="invalid-tooltip">Please include a four-digit Postcode.</div>
          </div>

          <div class="col-12 col-md-6 form-group pr-xl-4 mb-4">
            <label for="state">State</label>
            <select class="form-control" id="state" required v-model="userInfo.attributes.address.state">
              <option disabled></option>
              <option value="QLD">Queensland</option>
              <option value="NSW">New South Wales</option>
              <option value="VIC">Victoria</option>
              <option value="ACT">Australian Capital Territory</option>
              <option value="SA">South Australia</option>
              <option value="NT">Northern Territory</option>
              <option value="WA">Western Australia</option>
              <option value="TAS">Tasmania</option>
            </select>
            <div class="invalid-tooltip">Please include your State.</div>
          </div>

          <div class="col-12 col-md-6 form-group pl-xl-4 mb-4">
            <label for="mobile">Mobile Number <small class="d-inline-block">(optional)</small></label>
            <phone-input id="mobile" v-model="userInfo.attributes.mobile_phone" />
            <test-phone-numbers />
          </div>
        </div>

        <div v-if="isUnder17" class="col-12 mb-4">
          <h2 class="h3 mb-4">Parent / Guardian Details</h2>
          <div class="row">
            <div class="col-12 col-sm-6 pr-xl-4 form-group">
              <label for="parentFirstName">First Name</label>
              <input type="text" class="form-control" id="parentFirstName" maxlength="40" required v-model="userInfo.guardian_first_name">
              <div class="invalid-tooltip">Please include the First Name.</div>
            </div>
            <div class="col-12 col-sm-6 pl-xl-4 form-group">
              <label for="parentLastName">Last Name</label>
              <input type="text" class="form-control" id="parentLastName" maxlength="40" required v-model="userInfo.guardian_last_name">
              <div class="invalid-tooltip">Please include the Last Name.</div>
            </div>
            <div class="col-12 col-sm-6 pr-xl-4 form-group">
              <label for="parentEmail">Email Address</label>
              <input type="email" class="form-control" id="parentEmail" maxlength="255" required v-model="userInfo.guardian_email">
              <div class="invalid-tooltip">Please include the Email Address.</div>
            </div>
            <div class="col-12 col-sm-6 pl-xl-4 form-group">
              <label for="mobile">Phone Number</label>
              <input type="text" class="form-control" id="mobile" maxlength="15" required v-model="userInfo.guardian_phone">
              <div class="invalid-tooltip">Please include the Phone Number.</div>
            </div>
          </div>
        </div>

        <div class="col-12">
          <h2 class="h3 mb-2">Set Your Password</h2>
          <p class="mb-4">Please set your new password. This will be used when logging into CampdraftONE in future. Make sure it’s easily remembered.</p>
        </div>
        <div class="col-12 col-md-6 form-group pr-xl-4">
          <label for="password">Password</label>
          <input type="password" class="form-control" id="password" maxlength="30" required pattern="([A-Za-z0-9]){6,}" v-model="password.password">
          <div class="invalid-tooltip">Please include a Password between 6 and 30 characters, using only letters and numbers.</div>
          <small class="d-block">Must be between 6 and 30 characters, using only letters and numbers</small>
        </div>
        <div class="col-12 col-md-6 form-group pl-xl-4">
          <label for="confirmPassword">Confirm Password</label>
          <input type="password" class="form-control" id="confirmPassword" maxlength="30" required pattern="([A-Za-z0-9]){6,}" v-model="password.confirmPassword">
          <div class="invalid-tooltip">Please ensure your Password matches.</div>
        </div>

        <div class="col-12 mb-md-4"><hr></div>
        <div class="col-12 d-lg-flex justify-content-end align-items-center">
          <p class="pr-lg-3 mb-2 mb-lg-0">Take me to step 2</p>
          <button :class="['btn btn-block-mob px-md-5', processing ? 'btn-primary-dark' : 'btn-primary']" type="submit" @click="handleSubmit">
            {{ !processing ? 'Confirm' : '' }}
            <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
          </button>
        </div>
      </div>
    </section>
  </form>
</template>

<script>
import axios from 'axios'
import HANDLESUBMIT from '@/mixins/formValidation.js'
import LEGACYBROWSERS from '@/mixins/legacyBrowsers.js'
import PhoneInput from '@/components/FormFields/PhoneInput'
import TestPhoneNumbers from '@/components/TestPhoneNumbers'

export default {
  components: { TestPhoneNumbers, PhoneInput },
  mixins: [HANDLESUBMIT, LEGACYBROWSERS],
  props: ['user'],
  data() {
    return {
      userInfo: this.user,
      processing: false,
      errors: '',
      errorStatus: '',
      dateOfBirth: ''
    }
  },
  mounted() { window.scrollTo(0, 0) },
  watch: {
    userInfo: {
      deep: true,
      immediate: true,
      handler() {
        if (this.userInfo.dob) {
          this.dateOfBirth = this.userInfo.dob
        }
      }
    }
  },
  methods: {
    confirm() {
      setTimeout(() => {
        if (this.formComplete) {
          this.saveData()
        }
      }, 10)
    },
    saveData() {
      this.processing = true
      const member = {
        id: this.userInfo.id,
        title: this.userInfo.title,
        first_name: this.userInfo.first_name,
        last_name: this.userInfo.last_name,
        dob: this.userInfo.dob,
        gender: this.userInfo.gender,
        street_1: this.userInfo.attributes.address.street_1,
        street_2: this.userInfo.attributes.address.street_2,
        suburb: this.userInfo.attributes.address.suburb,
        postcode: this.userInfo.attributes.address.postcode,
        state: this.userInfo.attributes.address.state,
        mobile_phone: this.userInfo.attributes.mobile_phone,
        password: this.password.password,
        password_confirmation: this.password.confirmPassword
      }
      if (this.isUnder18) {
        member.guardian_consent = true
        member.guardian_first_name = this.userInfo.guardian_first_name
        member.guardian_last_name = this.userInfo.guardian_last_name
        member.guardian_phone = this.userInfo.guardian_phone
        member.guardian_email = this.userInfo.guardian_email
      }
      axios.post(`${this.$api}/onboarding/primary`, member).then((response) => {
        this.processing = false
        // eslint-disable-next-line
        console.log(response)
        if (response.data.status === 'success') {
          // Done
          this.$emit('proceed')
          this.errors = ''
          this.errorStatus = ''
        } else if (response.data.status === 'validation_error' && response.data.errors.dob) {
          // DoB
          this.success = ''
          this.errorStatus = 'validation_error'
          this.errors = `Please include your Date of Birth in the format${this.isLegacy() ? ' yyyy-mm-dd' : ' dd/mm/yyyy'}.`
        } else {
          // Error
          this.success = ''
          this.errors = 'Please ensure all fields are completed.'
        }
      }).catch((error) => {
        this.processing = false
        this.success = ''
        this.errors = 'There was a server error. Please try again later.'
        this.errorStatus = ''
        // eslint-disable-next-line
        console.log(error)
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/Account/LoginOnce/LoginOnce1.vue